/**
 * Toast utilities — Scholarly Modernist styled toasts via Sonner v2.
 *
 * Design spec (from design handoff):
 *   success — green tint bg, green border, dark green text
 *   error   — red tint bg, red border, dark red text
 *   warning — amber tint bg, amber border, dark amber text
 *   info    — blue tint bg, blue border, dark blue text
 *
 * Styling is applied via `globals.css` `@layer utilities` overrides on
 * `[data-sonner-toast][data-type]` — no custom renderer needed.
 * Dark mode handled automatically by the CSS overrides.
 *
 * RTL-aware position: top-left for Arabic, top-right for English.
 *
 * All components and mutations use these helpers — never call sonner directly.
 */
import { toast as sonnerToast } from "sonner";
import { getEffectiveDir } from "@/shared/lib/direction";

/** RTL-aware position: top-left for Arabic, top-right for English. */
function getPosition(): "top-left" | "top-right" {
  return getEffectiveDir() === "rtl" ? "top-left" : "top-right";
}

interface ToastAction {
  /** Button label (already localized). */
  label: string;
  /** Click handler — runs the action then auto-dismisses the toast. */
  onClick: () => void;
}

interface ToastOptions {
  /** Duration in ms. Defaults to 4000. */
  duration?: number;
  /** Optional description (subtitle) shown below the title. */
  description?: string;
  /** Optional inline action button (e.g. an Undo affordance). */
  action?: ToastAction;
  /** Stable id to dedupe repeated toasts — reusing one updates in place instead of stacking. */
  id?: string | number;
}

/**
 * Shows a success toast.
 * Example: toast.success("Title submitted", { description: "Manuscript sent for review." })
 */
function success(title: string, opts?: ToastOptions): string | number {
  return sonnerToast.success(title, {
    position: getPosition(),
    duration: opts?.duration ?? 4000,
    description: opts?.description,
    action: opts?.action,
    id: opts?.id,
  });
}

/**
 * Shows an error toast.
 * Example: toast.error("Upload failed", { description: "File exceeds the 50 MB limit." })
 */
function error(title: string, opts?: ToastOptions): string | number {
  return sonnerToast.error(title, {
    position: getPosition(),
    duration: opts?.duration ?? 4000,
    description: opts?.description,
    action: opts?.action,
    id: opts?.id,
  });
}

/**
 * Shows a warning toast.
 * Example: toast.warning("Approval pending", { description: "Account awaiting admin review." })
 */
function warning(title: string, opts?: ToastOptions): string | number {
  return sonnerToast.warning(title, {
    position: getPosition(),
    duration: opts?.duration ?? 4000,
    description: opts?.description,
    action: opts?.action,
    id: opts?.id,
  });
}

/**
 * Shows an info toast.
 * Example: toast.info("New ISBN assigned", { description: "978-614-00-2341-7 linked to your title." })
 */
function info(title: string, opts?: ToastOptions): string | number {
  return sonnerToast.info(title, {
    position: getPosition(),
    duration: opts?.duration ?? 4000,
    description: opts?.description,
    action: opts?.action,
    id: opts?.id,
  });
}

/** Dismiss a specific toast or all toasts. */
function dismiss(toastId?: string | number): void {
  sonnerToast.dismiss(toastId);
}

export const toast = { success, error, warning, info, dismiss };
